import { useEffect, useState } from 'react'
import { useAuth } from 'context/AuthContext'
import { useFetch } from 'utils/fetch-helper'
import { FetchMethods } from 'utils/types'
import { ModalDataAttributes } from '../types'
import FilterSelector from 'components/DropdownSelectors/FilterSelector'
import Translation from 'components/Translation'
import DataTable from 'components/DataTable'
import { classNames } from '@progress/kendo-react-common'
import {
    convertMinsToHrsMins,
    toHHMM,
    toHHMM12,
} from 'utils/string-localization-helper'
import { getTranslation } from 'utils/language-helper'
import { useAppContext } from 'context/AppContext'
import { Popup } from '@progress/kendo-react-popup'
import AlertPrompt from 'components/AlertPrompt'
import { MembershipApiItem } from 'types/membership'
import { MembershipClient } from 'types/membership-client'
import { mapMembershipAndClientMembership } from 'services/memberships-client'
import Pagination, { IPagination } from 'components/Pagination/Pagination'
import { useTenantContext } from 'context/TenantContext'

const Members = ({
    attributes,
    setAttributes,
    onCancel,
    onPrevious,
}: {
    attributes?: ModalDataAttributes
    setAttributes(r: ModalDataAttributes): void
    onCancel(): void
    onPrevious(): void
}) => {
    const {
        // MembershipEditModal,
        CreateClientModal: ClientModal,
        MembershipAddModal,
        MembershipModal,
    } = useAuth()
    const { tenant } = useTenantContext()

    const [members_data, setMemberData] = useState<any>([])
    const [selectedClasses, setSelectedClasses] = useState<string[]>([])
    const [popup_anim_css, setPopAmin] = useState<string>('bg-opacity-0')
    const [is_show, setShow] = useState<boolean>(false)
    const [filtered_membership_clients, setFilteredMembershipClients] =
        useState<MembershipClient[]>([])

    const [memberships, setMemberships] = useState<MembershipApiItem[]>([])
    const [pageSize, setPageSize] = useState(10)
    const [currentPage, setCurrentPage] = useState(0)

    const header_titles = [
        'client',
        'email',
        'purchased_on',
        'expiry_renew_date',
        'membership_status',
        'amount_remaining',
        // '',
    ]

    const [membership_clients, setMembershipClients] = useState<
        MembershipClient[]
    >([])

    const { translations, loadComponentTranslations } = useAppContext()
    const { data, doFetch: refetch } = useFetch(
        `/v1/memberships/clients?membershipId=${attributes?.id}&size=${pageSize}&page=${currentPage}`,
        FetchMethods.GET,
        !!attributes?.id
    )

    // Locations
    const { data: locations_api_response } = useFetch(
        `/v1/locations/tenant-id/?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id
    )
    const paginationProps: IPagination = {
        totalPages: data?.totalPages,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalRecords: data?.totalElements,
    }
    // TODO: Replace members_filter to use membership_clients
    let members_filter = data?.content?.filter(function (e: any) {
        return e?.membershipId == attributes?.id
    })

    useEffect(() => {
        if (
            data?.content?.length > 0 &&
            locations_api_response?.content?.length > 0
        ) {
            let fetched_membership_clients: MembershipClient[] = []
            fetched_membership_clients = data.content.map(
                (mc: MembershipClient) => {
                    let membership =
                        attributes?.edit_membership_api as MembershipApiItem
                    if (membership) {
                        mapMembershipAndClientMembership(membership, mc)
                        const location =
                            locations_api_response.content?.find(
                                (l: MembershipApiItem) =>
                                    l.id == membership.primaryLocationId
                            )
                        mc.location = location?.name || 'All'
                    }
                    return mc
                }
            )

            setMembershipClients(fetched_membership_clients)
        }
    }, [data, locations_api_response])

    // TODO: AOT-1344
    function handleEdit(member_api_arg: any) {
        ClientModal.setAttributes({ //TODO: refactor this
            email: member_api_arg?.clientEmail,
            idx: 0 
        })
        ClientModal.open()
        MembershipModal.close()
    }

    const timeFormat = (hour: any, minute: any, mileseconds: any) => {
        return toHHMM12(
            new Date(
                new Date().getFullYear(),
                new Date().getMonth(),
                new Date().getDate(),
                hour,
                minute,
                mileseconds
            )
        )
    }
    const dateFormat = (arg: any) => {
        return arg
            ? new Date(arg).toLocaleDateString('en-US', {
                  month: 'long',
                  day: '2-digit',
                  year: 'numeric',
              })
            : '-'
    }

    const headers = header_titles.map((title) => {
        return {
            label: getTranslation(title, translations),
            classNames:
                'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
        }
    })

    const rows: HTMLTableRowElement[] = (members_filter || []).map(
        (item: any, idx: any) => {
            let bg_status
            item.status == 'cancelled'
                ? (bg_status = 'bg-primary-lighter text-grey-600 ')
                : item.status == 'active'
                ? (bg_status = 'bg-green-100 text-green-900 px-7 ')
                : item.status == 'current '
                ? (bg_status = 'bg-green-100 text-green-900 px-7 ')
                : item.status == 'lapsed'
                ? (bg_status = 'bg-primary-lighter text-grey-600 ')
                : item.status == 'expired'
                ? (bg_status = 'bg-red-100 text-orange-600 ')
                : item.status == 'renewal_failed_maxed_out'
                ? (bg_status = 'bg-red-100 text-orange-600 px-4 ')
                : ''
            return (
                <tr
                    key={idx}
                    className={classNames(
                        idx % 2 ? 'bg-primary-lighter bg-opacity-30' : '',
                        'hover:bg-secondary hover:bg-opacity-10'
                    )}
                    onClick={() => handleEdit(item)}
                >
                    {' '}
                    <td className="px-5 py-2 align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            {/* <input
                                type="checkbox"
                                checked={selectedClasses.includes(item.id)}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedClasses((prev) => [
                                            ...prev,
                                            item.id,
                                        ])
                                    } else {
                                        setSelectedClasses((prev) =>
                                            prev.filter((i) => i != item.id)
                                        )
                                    }
                                }}
                                className="h-7 w-7 mr-2 border-gray-300 rounded text-primary-light focus:ring-primary-light"
                            /> */}
                            <span
                                className="text-sm "
                                style={{ color: '#3C3C3C' }}
                            >
                                {item.clientFirstName +
                                    ' ' +
                                    item.clientLastName}
                            </span>
                        </div>
                    </td>
                    <td className="px-5 py-2 align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            <span
                                className="text-sm"
                                style={{ color: '#3C3C3C' }}
                            >
                                {item.clientEmail}
                            </span>
                        </div>
                    </td>
                    <td className="px-5 py-2  align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            <span
                                className="text-sm"
                                style={{ color: '#3C3C3C' }}
                            >
                                {dateFormat(item.startsAt)}
                            </span>
                        </div>
                    </td>
                    <td className="px-5 py-2  align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            <span
                                className="text-sm"
                                style={{ color: '#3C3C3C' }}
                            >
                                {dateFormat(item.expiresAt)}
                            </span>
                        </div>
                    </td>
                    <td className="px-5 py-2  align-middle whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            <span
                                className={`${bg_status} px-4 py-1 inline-flex text-xs leading-5 font-bold rounded-full uppercase`}
                            >
                                {item.status == 'renewal_failed_maxed_out'
                                    ? 'RENEWAL FAILED'
                                    : item.status}
                            </span>
                        </div>
                    </td>
                    <td className="px-6 py-2  align-top whitespace-nowrap">
                        <div className="flex items-center gap-3">
                            <span
                                className="text-sm"
                                style={{ color: '#3C3C3C' }}
                            >
                                {item.remaining}
                            </span>
                        </div>
                    </td>
                    {/* <td className="px-6 py-1  align-top whitespace-nowrap flex items-center gap-2">
                        <button
                            type="button"
                            className="bg-primary-lighter bg-opacity-50 text-xl text-primary-light h-10 w-10 rounded-lg"
                        >
                            <i className="feather-edit"></i>
                        </button>
                        <button
                            type="button"
                            className="text-red-500 bg-red-100 h-10 w-10 rounded-lg text-xl feather-trash-2"
                        ></button>
                    </td> */}
                </tr>
            ) as unknown as HTMLTableRowElement
        }
    )

    const renderPopUpBody = () => {
        return (
            <div
                className="flex flex-col items-center h-32 w-80 items-center justify-between"
                style={{ margin: '0 auto' }}
            >
                <div>
                    <span className="text-base font-medium">
                        {/* {staff_data?.staff_name}({staff_data?.location}) */}
                    </span>
                    <div className="w-72 h-8 bg-red-50 rounded-xl flex items-center text-center justify-center">
                        {/* {staff_data?.date}  */}- Aerial yoga stretch
                    </div>
                </div>

                <p>
                    Note: All class schedules under this series will also be
                    removed upon deletion.
                </p>
            </div>
        )
    }

    return (
        <>
            <Popup
                show={is_show}
                popupAlign={{
                    horizontal: 'center',
                    vertical: 'top',
                }}
                animate={true}
                popupClass={classNames(
                    'bg-black h-screen transition ease-in-out duration-200 flex flex-row items-center justify-center',
                    popup_anim_css
                )}
                style={{
                    width: '100%',
                }}
                className="z-[102]"
            >
                <AlertPrompt
                    show={is_show}
                    title="are_you_sure_delete_schedule"
                    body={renderPopUpBody()}
                    actions={[
                        <button
                            type="button"
                            key="cancel"
                            className="border border-gray-200 rounded-lg h-12 text-gray-500 grow"
                            // onClick={() => {
                            //     setConfirmDelete(false)
                            // }}
                        >
                            <Translation content_key="cancel" />
                        </button>,
                        <button
                            type="button"
                            key="confirm"
                            className="bg-primary rounded-lg w-lg text-white h-12 grow"
                            // onClick={() => deleteConfirmClass()}
                        >
                            <Translation content_key="delete" />
                        </button>,
                    ]}
                />
            </Popup>
            <div className="">
                <div style={{ overflow: 'auto' }}>
                    <div className="flex gap-5">
                        {/* <FilterSelector
                            items={[]}
                            is_search_enabled={true}
                        /> */}
                    </div>
                    {/* <div className="border-y-2 mb-4 mt-4"> */}
                    <div className="mb-4">
                        <div className="flex items-center gap-5 mb-2 ml-5">
                            {/* <input
                                type="checkbox"
                                checked={
                                    selectedClasses.length ===
                                    members_filter?.length
                                }
                                className="h-7 w-7 mr-2 border-gray-300 rounded text-primary-light focus:ring-primary-light"
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedClasses(
                                            members_filter?.map(
                                                (item: any) => item.id
                                            )
                                        )
                                    } else {
                                        setSelectedClasses([])
                                    }
                                }}
                            /> */}
                            {/* <button
                                className=" bg-primary-light bg-opacity-10 text-primary-light rounded-lg p-2 "
                                type="button"
                            >
                                <i className="feather-edit-2 mr-2" />
                                <Translation content_key="edit" />
                            </button>
                            <button
                                className=" bg-primary-light bg-opacity-10 text-primary-light rounded-lg p-2 "
                                type="button"
                            >
                                <i className="feather-trash-2 mr-2" />
                                <Translation content_key="delete" />
                            </button> */}
                        </div>
                        <DataTable rows={rows} columns={headers} />{' '}
                    </div>{' '}
                </div>{' '}
                <Pagination {...paginationProps} />
                <div className="pt-6 flex justify-end">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="border border-gray-300 rounded-lg  inline-block btn-gap"
                            onClick={() => {
                                onCancel()
                            }}
                        >
                            <Translation
                                content_key="close"
                                translations={translations}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Members
